import React from 'react'
import { SideBarStyles } from '../../../Logs/SideBarLogs/styles'
import { Column, Row } from '../../../../../components/commons'
import { Content, UsersSearchStyles } from './styles'

import defaultPhoto from '../../../../../assets/images/default-image.png'
import { ModalDeleteRef } from '..'
import SearchGroup from './SearchGroup'
import { api } from '../../../../../services/api'
import useToast from '../../../../../hooks/useToast'
import { ButtonIcon } from '../../../../../components/Table/Pagination/styles'
import { FaTrash } from 'react-icons/fa6'
import { Group } from '../../../../../services/groups.service'
import { useUserStore } from '../../../../../store/user'
import { usePermissions } from '../../../../../hooks/usePermissions'

export interface GroupsSearchProps {
  fileId: string[]
  menuPortalTarget?: any
  menuPosition?: any
}

export interface RoleChangeProps {
  _id?: string
  role: string
  documentId: string[]
  refetch: () => void
}

export const GroupItem = ({
  item,
  handleSubmitDelete,
  onReapplyPermissions,
  isReapplying,
  relatedDocumentsCount,
}: {
  item: Group
  handleSubmitDelete: (user: string) => void
  onReapplyPermissions?: (groupId: string) => void
  isReapplying?: boolean
  relatedDocumentsCount?: number
}) => {
  const { canEditPermissions } = usePermissions()

  return (
    <>
      <UsersSearchStyles.Content>
        <UsersSearchStyles.Name>{item.name}</UsersSearchStyles.Name>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {relatedDocumentsCount && relatedDocumentsCount > 0 && canEditPermissions && onReapplyPermissions && (
            <ButtonIcon 
              onClick={() => onReapplyPermissions(item?._id)}
              disabled={isReapplying}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                fontSize: '12px',
                padding: '4px 8px',
                borderRadius: '4px',
                border: 'none',
                cursor: isReapplying ? 'not-allowed' : 'pointer',
                opacity: isReapplying ? 0.6 : 1
              }}
              title={`Reaplicar permissões a ${relatedDocumentsCount} documento(s) relacionados`}
            >
              {isReapplying ? '...' : 'Reaplicar'}
            </ButtonIcon>
          )}
          {canEditPermissions && (
            <ButtonIcon onClick={() => handleSubmitDelete(item?._id)}>
              <FaTrash size={20} />
            </ButtonIcon>
          )}
        </div>
      </UsersSearchStyles.Content>
    </>
  )
}

const GroupsSearch: React.FunctionComponent<GroupsSearchProps> = ({
  fileId,
  menuPosition,
  menuPortalTarget,
}) => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [isLoadingCreate, setIsLoadingCreate] = React.useState(false)
  const [groups, setGroup] = React.useState<Group[]>([])
  const [isReapplying, setIsReapplying] = React.useState(false)
  const [relatedDocuments, setRelatedDocuments] = React.useState<string[]>([])

  const modalDeleteRef = React.useRef<ModalDeleteRef>(null)
  const toast = useToast()

  // Função para buscar todos os documentos relacionados a uma pasta
  const getRelatedDocuments = async (documentId: string): Promise<string[]> => {
    try {
      console.log('Buscando documentos relacionados para:', documentId)
      
      // Buscar todos os documentos do sistema
      const response = await api.get('/documents', {
        params: {
          page: 1,
          perPage: 5000,
          directory: JSON.stringify(['/']),
        },
      })
      
      if (response.data?.data) {
        const allDocuments = response.data.data
        
        // Filtrar documentos que pertencem à pasta selecionada
        const relatedDocs = allDocuments.filter((doc: any) => {
          // Verificar se é filho direto ou indireto da pasta
          const isDirectChild = doc.parent === documentId || 
                               doc.parentId === documentId || 
                               doc.folderId === documentId ||
                               doc.objectId === documentId ||
                               (doc.directory && doc.directory.includes(documentId))
          
          // Verificar se o path contém o ID da pasta (para hierarquia)
          const isInPath = doc.path && doc.path.includes(documentId)
          
          // Verificar se o documento está dentro da pasta baseado no path
          const isInFolderPath = doc.path && doc.path !== '/' && 
                                doc.path.split('/').some((segment: string) => segment === documentId)
          
          console.log(`Documento ${doc.name}:`, {
            path: doc.path,
            isDirectChild,
            isInPath,
            isInFolderPath,
            isRelated: isDirectChild || isInPath || isInFolderPath
          })
          
          return isDirectChild || isInPath || isInFolderPath
        })
        
        const relatedIds = relatedDocs.map((doc: any) => doc._id)
        console.log('Documentos relacionados encontrados:', relatedIds.length)
        
        return relatedIds
      }
      
      return []
    } catch (error) {
      console.error('Erro ao buscar documentos relacionados:', error)
      return []
    }
  }

  const handleGetData = async () => {
    try {
      if (fileId.length > 1) {
        setGroup([])
        return
      }
      setIsLoading(true)

      const { data: response } = await api.get(`/documents/${fileId[0]}/groups`)

      setGroup(response?.groups || [])
      
      // Buscar documentos relacionados se for uma pasta
      const relatedDocs = await getRelatedDocuments(fileId[0])
      setRelatedDocuments(relatedDocs)
    } catch (error) {
      console.error('Erro ao carregar grupos:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitDelete = async (userId: string) => {
    try {
      setIsLoadingCreate(true)

      await Promise.all(
        fileId.map(async (file) => {
          const { data: response } = await api.delete(`/documents/${file}/groups/${userId}`)

          let payloadGroups = groups.filter((users: any) => users?.userId !== response._id)
          setGroup(payloadGroups)
        }),
      )

      handleGetData()
      toast.successToast('Usuario removido com sucesso!')
      modalDeleteRef.current?.closeModal()
    } catch (error) {
      toast.errorToast('Usuário ja cadastrado nesse documento!')
    } finally {
      setIsLoadingCreate(false)
    }
  }

  const onSelectGroup = async (selectedUser: any, reset: () => void) => {
    try {
      setIsLoadingCreate(true)
      let addedCount = 0;
      let existingCount = 0;

      await Promise.all(
        fileId.map(async (file: string) => {
          try {
            await api.post(`/documents/${file}/groups`, {
              groupId: selectedUser,
            })
            addedCount++;
          } catch (error) {
            existingCount++;
          }
        }),
      )

      handleGetData()

      if (addedCount > 0 && existingCount === 0) {
        toast.successToast('Grupo atribuído com sucesso!')
      } else if (addedCount > 0 && existingCount > 0) {
        toast.warningToast(`Grupo atribuído a ${addedCount} documento(s). Já estava cadastrado em ${existingCount}.`)
      } else if (existingCount > 0) {
        toast.warningToast('Grupo já está cadastrado no(s) documento(s) selecionado(s)!')
      }

      reset()
    } catch (error) {
      console.log(error)
      toast.errorToast('Ocorreu um erro ao atribuir o grupo.')
    } finally {
      setIsLoadingCreate(false)
    }
  }

  // Função para reaplicar permissões a todos os documentos relacionados
  const reapplyPermissions = async (groupId: string) => {
    try {
      setIsReapplying(true)
      
      // Aplicar permissão ao documento principal
      const mainDocuments = [...fileId]
      
      // Aplicar permissão aos documentos relacionados
      const allDocuments = [...mainDocuments, ...relatedDocuments]
      
      console.log('Reaplicando permissões para:', allDocuments.length, 'documentos')
      
      const results = await Promise.allSettled(
        allDocuments.map(async (docId: string) => {
          try {
            await api.post(`/documents/${docId}/groups`, {
              groupId: groupId,
            })
            return { docId, success: true }
          } catch (error) {
            console.log(`Erro ao aplicar permissão no documento ${docId}:`, error)
            return { docId, success: false, error }
          }
        })
      )
      
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length
      const failed = results.filter(r => r.status === 'rejected' || !r.value.success).length
      
      if (successful > 0) {
        toast.successToast(`Permissões reaplicadas com sucesso em ${successful} documento(s)!`)
      }
      
      if (failed > 0) {
        toast.errorToast(`${failed} documento(s) já possuem esta permissão`)
      }
      
      // Recarregar dados
      handleGetData()
      
    } catch (error) {
      console.error('Erro ao reaplicar permissões:', error)
      toast.errorToast('Erro ao reaplicar permissões!')
    } finally {
      setIsReapplying(false)
    }
  }

  React.useEffect(() => {
    handleGetData()
  }, [fileId])

  return (
    <Column style={{ gap: 15 }}>
      <Row justify='space-between'>
        <SideBarStyles.Title>Grupos Permitidos</SideBarStyles.Title>
      </Row>
      <SearchGroup
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        loadingSubmit={isLoading || isLoadingCreate}
        onSelectGroup={onSelectGroup}
      />
      <Content>
        <>
          {relatedDocuments.length > 0 && (
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: '#e3f2fd', 
              borderRadius: '4px', 
              marginBottom: '10px',
              fontSize: '12px',
              color: '#1976d2'
            }}>
              📁 {relatedDocuments.length} documento(s) relacionado(s) encontrado(s)
            </div>
          )}
          {fileId.length > 1 ? (
            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
              A visualização da lista de grupos não é suportada na seleção de múltiplos arquivos simultaneamente. Mas você ainda pode buscar e atribuir novos grupos a todos os arquivos selecionados.
            </div>
          ) : groups.length > 0 ? (
            [...groups].sort((a, b) => a.name.localeCompare(b.name)).map((group, index) => (
              <GroupItem 
                key={index} 
                item={group} 
                handleSubmitDelete={handleSubmitDelete}
                onReapplyPermissions={reapplyPermissions}
                isReapplying={isReapplying}
                relatedDocumentsCount={relatedDocuments.length}
              />
            ))
          ) : (
            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
              Nenhum grupo com acesso encontrado
            </div>
          )}
        </>
      </Content>
    </Column>
  )
}

export default GroupsSearch
